import {
  compile as vueCompile,
  defineComponent,
  computed,
  type Component,
  getCurrentInstance,
} from 'vue';
import type { CellFormatterConfig, CellEditorConfig } from './types';
import { isObject, isString } from 'lodash-es';

const templateCache = new Map<string, Component>();

export function compileFormatterTemplate(templateStr: string): Component {
  const cached = templateCache.get('f:' + templateStr);
  if (cached) {
    return cached;
  }

  const renderFn = vueCompile(templateStr);

  const component = defineComponent({
    name: 'CompiledTemplateFormatter',
    props: {
      value: { default: null },
      column: { type: Object, default: () => ({}) },
      row: { type: Object, default: () => ({}) },
      globs: { type: Object, default: () => ({}) },
      messages: { type: Object, default: () => ({}) },
    },
    setup(props) {
      const instance = getCurrentInstance();
      const filters =
        instance?.appContext.config.globalProperties.$filters || {};
      return {
        col: computed(() => props.column),
        ...filters,
      };
    },
    render: renderFn as any,
  });

  templateCache.set('f:' + templateStr, component);
  return component;
}

export function compileEditorTemplate(templateStr: string): Component {
  const cached = templateCache.get('e:' + templateStr);
  if (cached) {
    return cached;
  }

  const renderFn = vueCompile(templateStr);

  const component = defineComponent({
    name: 'CompiledTemplateEditor',
    props: {
      modelValue: { default: null },
      column: { type: Object, default: () => ({}) },
      row: { type: Object, default: () => ({}) },
      rowModel: { type: Object, default: null },
      table: { type: Object, default: null },
      globs: { type: Object, default: () => ({}) },
      messages: { type: Object, default: () => ({}) },
      autofocus: { type: Boolean, default: false },
    },
    emits: ['update:modelValue'],
    setup() {
      const instance = getCurrentInstance();
      const filters =
        instance?.appContext.config.globalProperties.$filters || {};
      return {
        ...filters,
      };
    },
    data() {
      return {
        internalValue: this.modelValue,
        formId: null as string | null,
      };
    },
    computed: {
      currentValue: {
        get(): any {
          return (this as any).internalValue;
        },
        set(v: any) {
          (this as any).internalValue = v;
          (this as any).$emit('update:modelValue', v);
        },
      },
      value(): any {
        return (this as any).modelValue;
      },
      col(): any {
        const self = this as any;
        const column = self.column || {};
        const table = self.table;
        const rowModel = self.rowModel;
        const rowForPayload = rowModel ?? self.row;
        const emit = (eventName: string, payload: any) => {
          table?.emitCellEvent?.(eventName, {
            ...(payload || {}),
            col: column,
            row: rowForPayload,
          });
        };
        return new Proxy(column, {
          get(target, prop: string) {
            if (prop === 'emit' || prop === 'emitCellEvent') {
              return emit;
            }
            if (prop === 'field') {
              return target.field ?? target.name;
            }
            if (prop === 'size') {
              return (target as any).size ?? 'sm';
            }
            return (target as any)[prop];
          },
        });
      },
      validationState(): boolean | null {
        const self = this as any;
        const rowModel = self.rowModel;
        const column = self.column || {};
        const fieldName = column.field || column.name;
        if (!rowModel?.errors?.value || !fieldName) {
          return null;
        }
        return rowModel.errors.value[fieldName] ? false : null;
      },
      isDisabled(): boolean {
        const self = this as any;
        const column = self.column || {};
        const row = self.row;
        if (typeof column.editorIsDisabled === 'function') {
          try {
            return !!column.editorIsDisabled(row);
          } catch {
            return false;
          }
        }
        if (column.editorIsDisabled === true) {
          return true;
        }
        return false;
      },
    },
    watch: {
      modelValue(v: any) {
        (this as any).internalValue = v;
      },
    },
    methods: {
      onChange(_event: any) {},
      onFieldChange(field: string, value: any) {
        const row = (this as any).row;
        if (row && field) {
          row[field] = value;
        }
      },
    },
    render: renderFn as any,
  });

  templateCache.set('e:' + templateStr, component);
  return component;
}

export function resolveFormatterComponent(
  config: string | CellFormatterConfig | undefined,
): Component | null {
  if (!config) {
    return null;
  }

  if (isString(config)) {
    return null;
  }

  if (isObject(config) && config.name === 'template' && config.value) {
    return compileFormatterTemplate(config.value);
  }

  return null;
}

export function resolveEditorComponent(
  config: string | CellEditorConfig | undefined,
): Component | null {
  if (!config) {
    return null;
  }

  if (isString(config)) {
    return null;
  }

  if (isObject(config) && config.name === 'template' && config.value) {
    return compileEditorTemplate(config.value);
  }

  return null;
}
